import { ReactElement } from 'react';

import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon, AppIcons, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { CareerCategory, CareerPath } from '@backrs_ui/common-ui/src/models/careerPath';
import { ContentNugget, NuggetVariant } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, SxProps, Theme } from '@mui/material';

import { getCompetencySticker } from '../NuggetCareerPath/NuggetDefaultCoverImage';

/**
 * Icon configuration for nugget variants that display an icon instead of an image.
 * Used by getNuggetImageUrl and components that need to render nugget icons at different sizes.
 */
export const NUGGET_VARIANT_ICON_CONFIG: Partial<
  Record<NuggetVariant, { icon: AppIcons; bgColor: ColorToken; iconColor: ColorToken }>
> = {
  major: { icon: 'Stand', bgColor: 'Error/400', iconColor: 'Colors/Foreground/fg-secondary' },
  scholarships: {
    icon: 'CurrencyDollarCircle',
    bgColor: 'Success/500',
    iconColor: 'Colors/Foreground/fg-secondary',
  },
  internship: {
    icon: 'Briefcase01',
    bgColor: 'Warning/400',
    iconColor: 'Colors/Foreground/fg-secondary',
  },
  industry: { icon: 'ShieldDollar', bgColor: 'Brand/700', iconColor: 'Colors/Base/White' },
  competency: {
    icon: 'BookOpen01',
    bgColor: 'Brand/200',
    iconColor: 'Colors/Foreground/fg-secondary',
  },
  'career-path': {
    icon: 'Palette',
    bgColor: 'Brand/500',
    iconColor: 'Colors/Foreground/fg-secondary',
  },
  coach: {
    icon: 'User01',
    bgColor: 'Brand/500',
    iconColor: 'Colors/Base/White',
  },
  'deep-dive': {
    icon: 'SearchLg',
    bgColor: 'Brand/700',
    iconColor: 'Colors/Base/White',
  },
};

/** Career path icon config - bgColor and iconColor are fixed, icon is dynamic based on category */
export const CAREER_PATH_ICON_CONFIG = {
  bgColor: 'Colors/Foreground/fg-brand-secondary' as ColorToken,
  iconColor: 'Colors/Base/Black' as ColorToken,
};

type GetTaskImageUrlProps = Readonly<{
  nugget: ContentNugget;
  careerCategories?: CareerCategory[];
  getCareerPathIcon?: (careerCategories: CareerCategory[], careerPath: CareerPath) => AppIcons;
  getStickerCardUrl?: (id: string) => string;
}>;

export type GetTaskImageUrlResult = Readonly<
  | {
      type: 'URL';
      url?: string;
    }
  | {
      type: 'COMPONENT';
      component: ReactElement;
    }
>;

export const getNuggetImageUrl = ({
  nugget,
  careerCategories,
  getCareerPathIcon,
  getStickerCardUrl,
}: GetTaskImageUrlProps): GetTaskImageUrlResult | undefined => {
  // Special handling for video variants
  if (nugget.variant === 'video-external' || nugget.variant === 'video-internal') {
    // Try preview_media first
    if (nugget.preview_media?.media_url) {
      return { type: 'URL', url: nugget.preview_media.media_url };
    }

    // For external videos (YouTube), try to generate thumbnail
    if (nugget.variant === 'video-external' && nugget.external_url) {
      const YOUTUBE_REGEX =
        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;

      const youtubeVideoId = nugget.external_url.match(YOUTUBE_REGEX)?.[5];

      if (youtubeVideoId) {
        return { type: 'URL', url: `https://img.youtube.com/vi/${youtubeVideoId}/0.jpg` };
      }
    }

    // For internal videos, try main media
    if (nugget.variant === 'video-internal' && nugget.media?.media_url) {
      return { type: 'URL', url: nugget.media.media_url };
    }

    return undefined;
  }

  if (nugget.variant === 'career-path' && nugget.career_path_info && careerCategories) {
    const icon = getCareerPathIcon?.(careerCategories, nugget.career_path_info);

    if (!icon) {
      return undefined;
    }

    const RenderedComponent = (
      <Box sx={stickerContainerCareerPathSx}>
        <AppIcon iconName={icon} diameter="32px" color="Colors/Base/Black" />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'major') {
    const RenderedComponent = (
      <Box sx={stickerContainerMajorSx}>
        <AppIcon iconName="Stand" diameter="32px" color="Colors/Foreground/fg-secondary" />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'scholarships') {
    const RenderedComponent = (
      <Box sx={stickerContainerScholarshipsSx}>
        <AppIcon
          iconName="CurrencyDollarCircle"
          diameter="32px"
          color="Colors/Foreground/fg-secondary"
        />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'internship') {
    const RenderedComponent = (
      <Box sx={stickerContainerInternshipSx}>
        <AppIcon iconName="Briefcase01" diameter="32px" color="Colors/Foreground/fg-secondary" />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'industry') {
    const RenderedComponent = (
      <Box sx={stickerContainerIndustrySx}>
        <AppIcon iconName="ShieldDollar" diameter="32px" color="Colors/Base/White" />
      </Box>
    );

    return {
      type: 'COMPONENT',
      component: RenderedComponent,
    };
  }

  if (nugget.variant === 'competency' && nugget.slug) {
    const competencySticker = getCompetencySticker(nugget.slug);

    return {
      type: 'URL',
      url: competencySticker,
    };
  }

  // Try preview_media first (most common for non-video variants)
  if (nugget.preview_media?.media_url) {
    return { type: 'URL', url: nugget.preview_media.media_url };
  }

  // Try main media
  if (nugget.media?.media_url) {
    return { type: 'URL', url: nugget.media.media_url };
  }

  // Variant-specific image properties
  switch (nugget.variant) {
    case 'profile-external':
      return { type: 'URL', url: nugget.external_profile_info?.profile_image_url };
    case 'profile-internal':
      return { type: 'URL', url: nugget.profile_info?.profile_image };
    case 'employer':
      return { type: 'URL', url: nugget.employer_info?.logo_media_url };
    case 'coach':
      return { type: 'URL', url: nugget.coach_info?.profile_image_url };
    case 'deep-dive':
      return { type: 'URL', url: nugget.deep_dive_info?.media };
    case 'tool':
      if (nugget.tool_info?.media?.media_url) {
        return { type: 'URL', url: nugget.tool_info?.media?.media_url };
      }

      if (nugget.tool_info?.tool_id) {
        const stickerCard = getStickerCardUrl?.(nugget.tool_info?.tool_id);

        if (stickerCard) {
          return { type: 'URL', url: stickerCard };
        }
      }

      return undefined;
    default:
      return undefined;
  }
};

const stickerContainerCareerPathSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});

const stickerContainerMajorSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Error/400'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});

const stickerContainerScholarshipsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Success/500'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});

const stickerContainerInternshipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Warning/400'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});

const stickerContainerIndustrySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  backgroundColor: backrs.colors['Brand/700'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: '50%',
});
